import { useTranslation } from 'react-i18next';

import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';

import type { ToastSeverity } from '../../lib/useToast';
import { MarkdownPlansPanel } from './MarkdownPlansPanel';
import { PriceChangeBatchesPanel } from './PriceChangeBatchesPanel';

export type BulkChangesView = 'batches' | 'markdown';

export interface BulkChangesPanelProps {
  view: BulkChangesView;
  onViewChange: (view: BulkChangesView) => void;
  /** `catalog.pricing.manage` — create a batch/plan. */
  canManage: boolean;
  /** `catalog.pricing.bulk` — dry-run/apply, activate/cancel; required IN ADDITION TO `canManage`. */
  canBulk: boolean;
  initialTargetListCode?: string;
  /** Opens `PriceChangeBatchDetailView` for this batch on mount (design spec §12.4, the card
   * drawer's "Open" deep link on its "Repricing" source-chip tooltip, `?tab=bulk&view=batches&
   * batch=`) — `PricingPage` already forces `view` to `'batches'` when this is set, mirroring
   * `initialTargetListCode`'s own wizard-opening seed. */
  initialBatchId?: string;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * The "Bulk changes" tab (design spec §8.8/§8.9, plan-platform-002 WP4.3) — a sub-view toggle
 * between bulk repricing batches and markdown plans, mirroring `PriceListsPanel`'s established
 * Lists/Coverage toggle pattern (the tab owns the toggle; `PricingPage` owns only the URL sync).
 */
export function BulkChangesPanel({
  view,
  onViewChange,
  canManage,
  canBulk,
  initialTargetListCode,
  initialBatchId,
  onResult,
}: BulkChangesPanelProps) {
  const { t } = useTranslation('catalog');

  return (
    <Box>
      <ToggleButtonGroup
        value={view}
        exclusive
        size="small"
        onChange={(_e, next: BulkChangesView | null) => {
          if (next) onViewChange(next);
        }}
        aria-label={t('bulkBatchesPanel.viewToggleAria')}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="batches">{t('bulkBatchesPanel.viewBatches')}</ToggleButton>
        <ToggleButton value="markdown">{t('bulkBatchesPanel.viewMarkdown')}</ToggleButton>
      </ToggleButtonGroup>

      {view === 'batches' ? (
        <PriceChangeBatchesPanel
          canManage={canManage}
          canBulk={canBulk}
          initialTargetListCode={initialTargetListCode}
          initialBatchId={initialBatchId}
          onResult={onResult}
        />
      ) : (
        <MarkdownPlansPanel canManage={canManage} canBulk={canBulk} onResult={onResult} />
      )}
    </Box>
  );
}
